import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { validateAadharNumber, validatePanCardNumber, validateRequiredField } from './ValidationUtils';

const SectionF = ({ formData: parentFormData, onDataChange }) => {
    // Initialize members from parentFormData
    const initialMembers = parentFormData?.member && Array.isArray(parentFormData.member)
        ? parentFormData.member
        : [];

    const [showModal, setShowModal] = useState(false);
    const [members, setMembers] = useState(initialMembers);
    
    // Refs to prevent circular updates
    const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
    const isUpdatingFromParentRef = useRef(false);
    const lastSentToParentRef = useRef(JSON.stringify({ member: members }));
    const membersRef = useRef(members);
    
    // Update ref when members change
    useEffect(() => {
        membersRef.current = members;
    }, [members]);
    const [editIndex, setEditIndex] = useState(null);
    const [formData, setFormData] = useState({
        RelationshipSubscriberName: '',
        M_FirstName: '',
        M_LastName: '',
        M_MiddleName: '',
        AadharNumber: '',
        PanCardNumber: '',
        M_DateOfBirth: ''
    });
    const [errors, setErrors] = useState({
        RelationshipSubscriberName: '',
        fullName: '',
        AadharNumber: '',
        PanCardNumber: '',
        M_DateOfBirth: '',
        maxLimit: ''
    });

    const memberRelationship = [
        "Spouse",
        "Dependent Mother (if living)",
        "Dependent Father (if living)",
        "Child 1 (if living)",
        "Child 2 (if living)",
        "Child 3 (if living)",
    ];

    // Update parent when members change
    useEffect(() => {
        if (isUpdatingFromParentRef.current) {
            return;
        }

        const currentData = JSON.stringify({ member: members });
        if (currentData !== lastSentToParentRef.current) {
            lastSentToParentRef.current = currentData;
            
            if (onDataChange) {
                onDataChange({ member: members });
            }
        }
    }, [members, onDataChange]);

    // Update from parent data (API response)
    useEffect(() => {
        if (!parentFormData) {
            return;
        }

        // Check if member exists (even if empty array)
        if (!parentFormData.hasOwnProperty('member')) {
            return;
        }

        const currentParentData = JSON.stringify(parentFormData);
        if (currentParentData === lastParentDataRef.current) {
            return;
        }

        // Handle both empty and non-empty arrays
        if (Array.isArray(parentFormData.member)) {
            const currentMembers = JSON.stringify(membersRef.current);
            const newMembers = JSON.stringify(parentFormData.member);
            
            if (currentMembers !== newMembers) {
                isUpdatingFromParentRef.current = true;
                setMembers(parentFormData.member);
                lastParentDataRef.current = currentParentData;
                
                setTimeout(() => {
                    isUpdatingFromParentRef.current = false;
                }, 100);
            }
        }
    }, [parentFormData]);

    // Function to split full name by spaces
    const splitFullName = (fullName) => {
        if (!fullName || !fullName.trim()) {
            return {
                M_FirstName: '',
                M_LastName: '',
                M_MiddleName: ''
            };
        }

        const parts = fullName.trim().split(/\s+/);
        const partCount = parts.length;

        if (partCount === 1) {
            return {
                M_FirstName: parts[0],
                M_LastName: '',
                M_MiddleName: ''
            };
        } else if (partCount === 2) {
            return {
                M_FirstName: parts[0],
                M_LastName: parts[1],
                M_MiddleName: ''
            };
        } else if (partCount === 3) {
            return {
                M_FirstName: parts[0],
                M_MiddleName: parts[1],
                M_LastName: parts[2]
            };
        } else {
            // More than 3 words: First = first word, Middle = second word, Last = rest joined
            return {
                M_FirstName: parts[0],
                M_MiddleName: parts[1],
                M_LastName: parts.slice(2).join(' ')
            };
        }
    };

    // Validation functions
    const validateRelationship = (value) => {
        if (!value || !value.trim()) {
            return 'Relationship is required';
        }
        return '';
    };

    const validateFullName = (value) => {
        if (!value || !value.trim()) {
            return 'Full Name is required';
        }
        if (value.trim().length < 2) {
            return 'Full Name must be at least 2 characters';
        }
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return 'Full Name should contain only letters';
        }
        return '';
    };

    const validateDateOfBirth = (value) => {
        if (!value || !value.trim()) {
            return 'Date of Birth is required';
        }
        return '';
    };

    const handleOpenModal = () => {
        // Check if limit reached
        if (members.length >= 4) {
            setErrors({
                RelationshipSubscriberName: '',
                fullName: '',
                AadharNumber: '',
                PanCardNumber: '',
                maxLimit: 'Maximum 4 family members can be added. Please delete a member to add a new one.'
            });
            return;
        }
        
        setShowModal(true);
        setEditIndex(null);
        setFormData({
            RelationshipSubscriberName: '',
            M_FirstName: '',
            M_LastName: '',
            M_MiddleName: '',
            AadharNumber: '',
            PanCardNumber: '',
            M_DateOfBirth: ''
        });
        setErrors({
            RelationshipSubscriberName: '',
            fullName: '',
            AadharNumber: '',
            PanCardNumber: '',
            M_DateOfBirth: '',
            maxLimit: ''
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditIndex(null);
        setFormData({
            RelationshipSubscriberName: '',
            M_FirstName: '',
            M_LastName: '',
            M_MiddleName: '',
            AadharNumber: '',
            PanCardNumber: '',
            M_DateOfBirth: ''
        });
        setErrors({
            RelationshipSubscriberName: '',
            fullName: '',
            AadharNumber: '',
            PanCardNumber: '',
            M_DateOfBirth: '',
            maxLimit: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Handle fullName splitting
        if (name === 'fullName') {
            const splitName = splitFullName(value);
            setFormData(prev => ({
                ...prev,
                ...splitName
            }));
            
            // Validate full name
            const fullNameError = validateFullName(value);
            setErrors(prev => ({
                ...prev,
                fullName: fullNameError
            }));
            return;
        }

        // Validate RelationshipSubscriberName
        if (name === 'RelationshipSubscriberName') {
            const error = validateRelationship(value);
            setErrors(prev => ({
                ...prev,
                RelationshipSubscriberName: error
            }));
        }

        // Validate AadharNumber
        if (name === 'AadharNumber') {
            const error = validateAadharNumber(value);
            setErrors(prev => ({
                ...prev,
                AadharNumber: error
            }));
        }

        // Validate PanCardNumber
        if (name === 'PanCardNumber') {
            const error = validatePanCardNumber(value);
            setErrors(prev => ({
                ...prev,
                PanCardNumber: error
            }));
        }

        // Validate Date of Birth
        if (name === 'M_DateOfBirth') {
            const error = validateDateOfBirth(value);
            setErrors(prev => ({
                ...prev,
                M_DateOfBirth: error
            }));
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        const fullName = `${formData.M_FirstName}${formData.M_MiddleName ? ' ' + formData.M_MiddleName : ''}${formData.M_LastName ? ' ' + formData.M_LastName : ''}`.trim();
        return !errors.RelationshipSubscriberName &&
               !errors.fullName &&
               !errors.AadharNumber &&
               !errors.PanCardNumber &&
               !errors.M_DateOfBirth &&
               formData.RelationshipSubscriberName &&
               fullName &&
               formData.AadharNumber &&
               formData.PanCardNumber &&
               formData.M_DateOfBirth;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Check maximum limit (4 members)
        if (editIndex === null && members.length >= 4) {
            setErrors({
                RelationshipSubscriberName: '',
                fullName: '',
                AadharNumber: '',
                PanCardNumber: '',
                maxLimit: 'Maximum 4 family members can be added. Please delete a member to add a new one.'
            });
            return;
        }

        // Validate all fields
        const fullName = `${formData.M_FirstName}${formData.M_MiddleName ? ' ' + formData.M_MiddleName : ''}${formData.M_LastName ? ' ' + formData.M_LastName : ''}`.trim();
        const relationshipError = validateRelationship(formData.RelationshipSubscriberName);
        const fullNameError = validateFullName(fullName);
        const aadharError = validateAadharNumber(formData.AadharNumber);
        const panCardError = validatePanCardNumber(formData.PanCardNumber);
        const dateOfBirthError = validateDateOfBirth(formData.M_DateOfBirth);

        setErrors({
            RelationshipSubscriberName: relationshipError,
            fullName: fullNameError,
            AadharNumber: aadharError,
            PanCardNumber: panCardError,
            M_DateOfBirth: dateOfBirthError,
            maxLimit: ''
        });

        if (relationshipError || fullNameError || aadharError || panCardError || dateOfBirthError) {
            return;
        }

        // Create member object with correct parameter names
        const memberData = {
            RelationshipSubscriberName: formData.RelationshipSubscriberName,
            M_FirstName: formData.M_FirstName,
            M_LastName: formData.M_LastName,
            M_MiddleName: formData.M_MiddleName,
            AadharNumber: formData.AadharNumber,
            PanCardNumber: formData.PanCardNumber,
            M_DateOfBirth: formData.M_DateOfBirth
        };

        if (editIndex !== null) {
            // Edit existing member - preserve existing fields
            const updatedMembers = [...members];
            updatedMembers[editIndex] = {
                ...members[editIndex], // Preserve existing fields
                ...memberData // Update with new data
            };
            setMembers(updatedMembers);
        } else {
            // Add new member
            setMembers([...members, memberData]);
        }

        handleCloseModal();
    };

    const handleEdit = (index) => {
        setEditIndex(index);
        const member = members[index];
        setFormData(member);
        
        // Validate existing data
        const fullName = `${member.M_FirstName}${member.M_MiddleName ? ' ' + member.M_MiddleName : ''}${member.M_LastName ? ' ' + member.M_LastName : ''}`.trim();
        const relationshipError = validateRelationship(member.RelationshipSubscriberName);
        const fullNameError = validateFullName(fullName);
        const aadharError = validateAadharNumber(member.AadharNumber);
        const panCardError = validatePanCardNumber(member.PanCardNumber);
        const dateOfBirthError = validateDateOfBirth(member.M_DateOfBirth || '');
        
        setErrors({
            RelationshipSubscriberName: relationshipError,
            fullName: fullNameError,
            AadharNumber: aadharError,
            PanCardNumber: panCardError,
            M_DateOfBirth: dateOfBirthError,
            maxLimit: ''
        });
        
        setShowModal(true);
    };

    const handleDelete = (index) => {
        if (window.confirm('Are you sure you want to delete this member?')) {
            const updatedMembers = members.filter((_, i) => i !== index);
            setMembers(updatedMembers);
            // Clear max limit error if member is deleted
            setErrors(prev => ({
                ...prev,
                maxLimit: ''
            }));
        }
    };

    return (
        <div className="section-container">
            <div className="card shadow-sm">
                <div className="card-body p-4">

                    {/* Add Member Button */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={handleOpenModal}
                                disabled={members.length >= 4}
                                style={{
                                    borderRadius: '25px',
                                    padding: '0.5rem 1.5rem',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    border: '2px solid #0d6efd',
                                    opacity: members.length >= 4 ? 0.5 : 1,
                                    cursor: members.length >= 4 ? 'not-allowed' : 'pointer'
                                }}
                                title={members.length >= 4 ? 'Maximum 4 members allowed' : 'Add Family Member'}
                            >
                                <FaPlus className="me-2" style={{ fontSize: '0.85rem' }} />
                                Add Member
                            </button>
                            <div style={{ fontSize: '0.875rem', color: members.length >= 4 ? '#dc3545' : '#666', fontWeight: '500' }}>
                                {members.length} / 4 Members
                            </div>
                        </div>
                        {errors.maxLimit && (
                            <div className="alert alert-warning" style={{ fontSize: '0.85rem', padding: '0.75rem', marginBottom: 0 }}>
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {errors.maxLimit}
                            </div>
                        )}
                    </div>

                    {/* Members Table */}
                    {members.length > 0 ? (
                        <div className="table-responsive" style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                <thead style={{ backgroundColor: '#0d6efd', color: 'white' }}>
                                    <tr>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>S.No</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Relationship</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Full Name</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Aadhar Number</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>PAN Card</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none' }}>Date of Birth</th>
                                        <th style={{ fontSize: '0.85rem', fontWeight: '600', padding: '1rem 0.75rem', borderBottom: 'none', textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody style={{ backgroundColor: '#fff' }}>
                                    {members.map((member, index) => {
                                        const fullName = `${member.M_FirstName || ''}${member.M_MiddleName ? ' ' + member.M_MiddleName : ''}${member.M_LastName ? ' ' + member.M_LastName : ''}`.trim();
                                        return (
                                            <tr key={index} style={{ 
                                                borderBottom: index !== members.length - 1 ? '1px solid #e9ecef' : 'none',
                                                transition: 'background-color 0.2s'
                                            }}>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    fontWeight: '500',
                                                    color: '#666',
                                                    textAlign: 'center'
                                                }}>
                                                    {index + 1}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666'
                                                }}>
                                                    {member.RelationshipSubscriberName || '-'}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    fontWeight: '500',
                                                    color: '#333'
                                                }}>
                                                    {fullName || '-'}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666'
                                                }}>
                                                    {member.AadharNumber || '-'}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666',
                                                    textTransform: 'uppercase',
                                                    fontWeight: '500'
                                                }}>
                                                    {member.PanCardNumber || '-'}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    color: '#666'
                                                }}>
                                                    {member.M_DateOfBirth || '-'}
                                                </td>
                                                <td style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.9rem 0.75rem',
                                                    textAlign: 'center'
                                                }}>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleEdit(index)}
                                                            title="Edit Member"
                                                            style={{ 
                                                                padding: '0.35rem 0.6rem',
                                                                backgroundColor: '#e7f1ff',
                                                                border: '1px solid #0d6efd',
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#0d6efd';
                                                                e.currentTarget.querySelector('svg').style.color = 'white';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#e7f1ff';
                                                                e.currentTarget.querySelector('svg').style.color = '#0d6efd';
                                                            }}
                                                        >
                                                            <FaEdit style={{ fontSize: '0.9rem', color: '#0d6efd' }} />
                                                        </button>
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => handleDelete(index)}
                                                            title="Delete Member"
                                                            style={{ 
                                                                padding: '0.35rem 0.6rem',
                                                                backgroundColor: '#ffe7e7',
                                                                border: '1px solid #dc3545',
                                                                borderRadius: '6px',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#dc3545';
                                                                e.currentTarget.querySelector('svg').style.color = 'white';
                                                            }}
                                                            onMouseOut={(e) => {
                                                                e.currentTarget.style.backgroundColor = '#ffe7e7';
                                                                e.currentTarget.querySelector('svg').style.color = '#dc3545';
                                                            }}
                                                        >
                                                            <FaTrash style={{ fontSize: '0.9rem', color: '#dc3545' }} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-5 px-4" style={{ 
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '2px dashed #dee2e6'
                        }}>
                            <i className="fas fa-user-friends" style={{ fontSize: '3rem', color: '#dee2e6', marginBottom: '1rem' }}></i>
                            <p style={{ color: '#6c757d', fontSize: '0.95rem', marginBottom: '0.5rem', fontWeight: '500' }}>
                                No family members added yet
                            </p>
                            <p style={{ color: '#adb5bd', fontSize: '0.85rem', marginBottom: 0 }}>
                                Click "Add Member" button to add a family member
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Member Modal */}
            {showModal && (
                <>
                    <div
                        className="modal-backdrop fade show"
                        style={{ zIndex: 10050, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                        onClick={handleCloseModal}
                    ></div>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        style={{ zIndex: 10055 }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content" style={{ opacity: 1 }}>
                                <div className="modal-header" style={{ backgroundColor: '#0d6efd', color: 'white' }}>
                                    <h5 className="modal-title" style={{ fontSize: '1rem', fontWeight: '600' }}>
                                        {editIndex !== null ? 'Edit Member' : 'Add Member'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={handleCloseModal}
                                    ></button>
                                </div>
                                <div className="modal-body" style={{ padding: '1.5rem' }}>
                                    <form onSubmit={handleSubmit}>
                                        {/* Relationship with Subscriber */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Relationship with Subscriber <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className={`form-select ${errors.RelationshipSubscriberName ? 'is-invalid' : ''}`}
                                                name="RelationshipSubscriberName"
                                                value={formData.RelationshipSubscriberName}
                                                onChange={handleInputChange}
                                                style={{ fontSize: '0.875rem' }}
                                            >
                                                <option value="">Select Relationship</option>
                                                {memberRelationship.map((option, idx) => (
                                                    <option key={idx} value={option}>{option}</option>
                                                ))}
                                            </select>
                                            {errors.RelationshipSubscriberName && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.RelationshipSubscriberName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Full Name */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Full Name <span className="text-danger">*</span>
                                                <small className="text-muted ms-2">(Will be split into First, Middle, Last based on spaces)</small>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.fullName ? 'is-invalid' : ''}`}
                                                name="fullName"
                                                value={`${formData.M_FirstName}${formData.M_MiddleName ? ' ' + formData.M_MiddleName : ''}${formData.M_LastName ? ' ' + formData.M_LastName : ''}`.trim()}
                                                onChange={handleInputChange}
                                                placeholder="Enter full name (e.g., 'John', 'John Doe', 'John Michael Doe')"
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                            {errors.fullName && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.fullName}
                                                </div>
                                            )}
                                        </div>

                                        {/* Aadhar Number */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Aadhar Number <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.AadharNumber ? 'is-invalid' : ''}`}
                                                name="AadharNumber"
                                                value={formData.AadharNumber}
                                                onChange={handleInputChange}
                                                placeholder="Enter Aadhar number"
                                                maxLength="12"
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                            {errors.AadharNumber && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.AadharNumber}
                                                </div>
                                            )}
                                            <small className="text-muted">12 digit Aadhar number</small>
                                        </div>

                                        {/* PAN Card */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                PAN Card <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.PanCardNumber ? 'is-invalid' : ''}`}
                                                name="PanCardNumber"
                                                value={formData.PanCardNumber}
                                                onChange={handleInputChange}
                                                placeholder="Enter PAN card number"
                                                maxLength="10"
                                                style={{ fontSize: '0.875rem', textTransform: 'uppercase' }}
                                            />
                                            {errors.PanCardNumber && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.PanCardNumber}
                                                </div>
                                            )}
                                            <small className="text-muted">Format: ABCDE1234F</small>
                                        </div>

                                        {/* Date of Birth */}
                                        <div className="mb-3">
                                            <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                                                Date of Birth <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className={`form-control ${errors.M_DateOfBirth ? 'is-invalid' : ''}`}
                                                name="M_DateOfBirth"
                                                value={formData.M_DateOfBirth}
                                                onChange={handleInputChange}
                                                max={new Date().toISOString().split('T')[0]}
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                            {errors.M_DateOfBirth && (
                                                <div className="invalid-feedback d-block" style={{ fontSize: '0.8rem' }}>
                                                    {errors.M_DateOfBirth}
                                                </div>
                                            )}
                                        </div>

                                        {/* Modal Footer */}
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={handleCloseModal}
                                                style={{ fontSize: '0.875rem', padding: '0.5rem 1.25rem' }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                                style={{ 
                                                    fontSize: '0.875rem', 
                                                    padding: '0.5rem 1.25rem',
                                                    opacity: isFormValid() ? 1 : 0.6,
                                                    cursor: isFormValid() ? 'pointer' : 'not-allowed'
                                                }}
                                                disabled={!isFormValid()}
                                            >
                                                {editIndex !== null ? 'Update Member' : 'Add Member'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SectionF;

